import React, { useEffect, useState } from "react";
import { CheckCircle2, CreditCard } from "lucide-react";
import { adminApi } from "@/lib/api";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [stripeKey, setStripeKey] = useState("");
  const [pubKey, setPubKey] = useState("");
  const [expressOn, setExpressOn] = useState(false);
  const [form, setForm] = useState({ store_email: "", store_phone: "", store_address: "", shipping_note: "", google_site_verification: "" });
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    adminApi.get("/admin/settings").then((r) => {
      setSettings(r.data);
      setPubKey(r.data.stripe_publishable_key || "");
      setExpressOn(!!r.data.express_checkout_enabled);
      setForm({
        store_email: r.data.store_email || "",
        store_phone: r.data.store_phone || "",
        store_address: r.data.store_address || "",
        shipping_note: r.data.shipping_note || "",
        google_site_verification: r.data.google_site_verification || "",
      });
    }).catch(() => {});
  };

  useEffect(load, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, express_checkout_enabled: expressOn };
      if (stripeKey.trim()) payload.stripe_secret_key = stripeKey.trim();
      if (pubKey.trim()) payload.stripe_publishable_key = pubKey.trim();
      const { data } = await adminApi.put("/admin/settings", payload);
      setSettings(data);
      setStripeKey("");
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <p className="ee-overline animate-pulse">Loading settings…</p>;

  const inputCls = "w-full bg-transparent border-b ee-hairline focus:border-[#D4AF37] outline-none py-3 text-[#FAFAF9] transition-colors placeholder:text-[#57534E]";

  return (
    <div className="max-w-2xl" data-testid="admin-settings">
      <p className="ee-overline mb-2">Configuration</p>
      <h1 className="font-display text-4xl tracking-tight mb-10">Settings</h1>

      <div className="border ee-hairline bg-[#1C1917] p-8 mb-8">
        <div className="flex items-center gap-3 mb-5">
          <CreditCard className="ee-gold-text" strokeWidth={1.3} />
          <h2 className="font-display text-2xl">Stripe Payments</h2>
        </div>
        <p className="text-sm text-[#A8A29E] mb-1">
          Current key: <span className="ee-gold-text">{settings.stripe_configured ? settings.stripe_secret_key_masked : "Using platform test key (sk_test_emergent)"}</span>
        </p>
        <p className="text-xs text-[#57534E] mb-5">Enter your own Stripe secret key to override. Leave blank to keep the current key.</p>
        <input
          type="password"
          placeholder="sk_live_… or sk_test_…"
          value={stripeKey}
          onChange={(e) => setStripeKey(e.target.value)}
          className={inputCls}
          data-testid="settings-stripe-key"
        />

        <div className="mt-7 pt-6 border-t ee-hairline">
          <p className="text-sm text-[#FAFAF9] mb-1">On-page Express Checkout (Apple Pay / Google Pay / Link)</p>
          <p className="text-xs text-[#57534E] mb-4">
            Add your Stripe <span className="text-[#A8A29E]">publishable</span> key and enable the wallet buttons on product pages.
            Apple Pay also requires registering your domain in the Stripe Dashboard. Status:{" "}
            <span className={settings.express_ready ? "text-emerald-400" : "text-amber-400"} data-testid="express-status">
              {settings.express_ready ? "Active" : "Not active"}
            </span>
          </p>
          <input
            placeholder="pk_live_… or pk_test_…"
            value={pubKey}
            onChange={(e) => setPubKey(e.target.value)}
            className={inputCls}
            data-testid="settings-stripe-pub-key"
          />
          <label className="flex items-center gap-3 mt-5 cursor-pointer select-none" data-testid="settings-express-toggle">
            <input type="checkbox" checked={expressOn} onChange={(e) => setExpressOn(e.target.checked)} className="accent-[#D4AF37] h-4 w-4" />
            <span className="text-sm text-[#A8A29E]">Show express wallet buttons on product pages</span>
          </label>
        </div>
      </div>

      <div className="border ee-hairline bg-[#1C1917] p-8">
        <h2 className="font-display text-2xl mb-6">Store Details</h2>
        <div className="space-y-6">
          <div>
            <label className="text-xs text-[#57534E] uppercase tracking-[0.14em]">Email</label>
            <input value={form.store_email} onChange={(e) => setForm({ ...form, store_email: e.target.value })} className={inputCls} data-testid="settings-email" />
          </div>
          <div>
            <label className="text-xs text-[#57534E] uppercase tracking-[0.14em]">Phone</label>
            <input value={form.store_phone} onChange={(e) => setForm({ ...form, store_phone: e.target.value })} className={inputCls} data-testid="settings-phone" />
          </div>
          <div>
            <label className="text-xs text-[#57534E] uppercase tracking-[0.14em]">Address</label>
            <input value={form.store_address} onChange={(e) => setForm({ ...form, store_address: e.target.value })} className={inputCls} data-testid="settings-address" />
          </div>
          <div>
            <label className="text-xs text-[#57534E] uppercase tracking-[0.14em]">Shipping note</label>
            <input value={form.shipping_note} onChange={(e) => setForm({ ...form, shipping_note: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="text-xs text-[#57534E] uppercase tracking-[0.14em]">Google site verification code</label>
            <input value={form.google_site_verification} onChange={(e) => setForm({ ...form, google_site_verification: e.target.value })} className={inputCls} data-testid="settings-google-verification" placeholder="content value from the Search Console HTML tag" />
            <p className="text-[11px] text-[#57534E] mt-1.5">Paste only the <code>content</code> value of the Search Console "HTML tag" verification. Sitemap: <code>/api/seo/sitemap.xml</code> · Merchant feed: <code>/api/seo/merchant-feed.xml</code></p>
          </div>
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="settings-save-btn" className="mt-8 bg-[#D4AF37] text-[#0C0A09] px-10 py-4 uppercase tracking-[0.15em] text-sm hover:bg-[#B5952F] transition-colors flex items-center gap-2 disabled:opacity-60">
        {saved ? (<><CheckCircle2 size={16} /> Saved</>) : saving ? "Saving…" : "Save Settings"}
      </button>
    </div>
  );
}
